import { milestoneBadgeStatusMap } from '@/constants/milestones';
import { getAfterCompletionRedirectPath } from '@/helpers/getAfterCompletionRedirectPath';
import { resolveMilestonePackageRoute } from '@/helpers/milestonePackageRoute';
import usePermissions from '@/hooks/usePermissions';
import { useProjectSubmittedBefore2026 } from '@/hooks/useProjectSubmittedBefore2026';
import { useGetPerformProgressQuery } from '@/services/organizations';
import { PalmettoIcon } from '@palmetto/dsm-components';
import { Badge, Box, Button, Card, useBreakpoint } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AccountMilestone,
  AccountRequirementDocument,
  AccountRequirementStatus,
  MilestonePackageRoute,
  MilestoneRequirement,
  MilestoneType,
  Packages,
  QueueDocumentResponse,
  QueueStatus,
  QueueType,
  QuoteStatus,
  UserPermissions,
} from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { useGetQueuesByAccountIdQuery } from '../../services/queues';
import { useGetQuotesQuery } from '../../services/quotes';
import { useIsOrgReadOnly } from '../auth/claimsUtils';

export const InstallMilestonePackageContainer = () => {
  const { id } = useParams<{ id: any }>();
  const { data: account } = useGetAccountQuery(id);
  const programType = account?.programType;
  const navigate = useNavigate();
  const { data: quotes, isLoading: areQuotesLoading } = useGetQuotesQuery(
    { id, programType },
    { skip: !programType },
  );

  const { isPhone } = useBreakpoint();

  const { data: queuesData } = useGetQueuesByAccountIdQuery({
    accountId: id,
    types: [QueueType.installPackage],
  });
  const queue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.installPackage);
  const { enableQcPriority = false } = useFlags();
  const targetDate = queue?.reviewSla?.current?.targetDate;
  const decisionDate = queue?.reviewSla?.current?.decisionDate;
  const formattedTarget = targetDate
    ? DateTime.fromISO(targetDate, { zone: 'utc' }).toLocal().toFormat("MMM d '·' h:mm a")
    : undefined;
  const userPermissions = usePermissions();
  const isLightReachQCTech = userPermissions.includes(UserPermissions.lightReachQCTech);
  const allowsAction = useIsOrgReadOnly();

  const installPackageRequirement = useMemo(() => {
    if (!account) {
      return;
    }

    const installMilestone = account.milestones?.find((m: AccountMilestone) => m.type === MilestoneType.install);
    const installPackageRequirement: AccountRequirementDocument = installMilestone?.requirements.find(
      (req: AccountRequirementDocument) => req.type === MilestoneRequirement.installPackage,
    );
    return installPackageRequirement;
  }, [account]);

  const exceptionInformation = account?.systemDesign?.toleranceException;

  const installPackageStatus = installPackageRequirement
    ? installPackageRequirement.status
    : AccountRequirementStatus.pending;
  const isInstallPackageCreated = installPackageStatus !== AccountRequirementStatus.pending;
  const isLoading = areQuotesLoading;
  const hasActiveQuote = isLoading
    ? false
    : Boolean((quotes || []).find((element: any) => element.status === QuoteStatus.active));
  const queueStatusIsConditionallyApproved = queue && queue.status === QueueStatus.conditionallyApproved;
  const { data: canPerformProgressBasedOnOrgStatus } = useGetPerformProgressQuery(id);
  const wasSubmittedBefore2026 = useProjectSubmittedBefore2026(account?.id);

  const handleClick = function () {
    const hasAttestment = Boolean(account?.systemDesign?.attestment);
    const route = resolveMilestonePackageRoute({
      accountId: id,
      programType,
      packageType: Packages.installPackage,
      hasActiveQuote,
      hasAttestment,
      wasSubmittedBefore2026,
    });
    navigate(route.path, route.state ? { state: route.state } : undefined);
  };

  const handleEditClickAction = () => {
    const routePath = `/accounts/${id}/installation-package/edit`;
    navigate(routePath);
  };

  const handleAfterCompletionRedirect = () => {
    const routePath = getAfterCompletionRedirectPath(id, MilestonePackageRoute.installPackage, isLightReachQCTech);
    navigate(routePath);
  };

  return (
    <Card margin="0 0 lg 0">
      <Box childGap="lg" padding="lg" alignItems="center" direction="row" justifyContent="space-between">
        <Box as="h3" fontWeight="medium" fontSize="md" disabled={!allowsAction}>
          Install Package
        </Box>
        {!isInstallPackageCreated && (
          <Button
            onClick={handleClick}
            disabled={!canPerformProgressBasedOnOrgStatus || !allowsAction}
            size="sm"
            variant="primary"
            tone="neutral"
            iconPrefix="add"
            isLoading={isLoading}
          >
            {!isPhone && 'Install Package'}
          </Button>
        )}
      </Box>
      <Box
        borderWidth="xs 0 0 0"
        borderColor="separator"
        background="primary"
        padding={{ base: 'md 0', tablet: '0' }}
        direction="row"
        width="100"
        alignItems="center"
      >
        {isInstallPackageCreated ? (
          <>
            <Box width={{ base: '100', tablet: '60' }} direction={{ base: 'column', tablet: 'row' }}>
              <Box
                alignItems="flex-start"
                width={{ base: '100', tablet: '30' }}
                padding={{ base: 'md lg', tablet: 'md md md lg' }}
              >
                {isInstallPackageCreated && (
                  <Badge
                    message={milestoneBadgeStatusMap[installPackageStatus].label}
                    variant={milestoneBadgeStatusMap[installPackageStatus].variant}
                  />
                )}
                {isInstallPackageCreated && exceptionInformation && (
                  <Box margin="sm 0 0 0">
                    <Badge message="Exception" variant="warning" />
                  </Box>
                )}
              </Box>
              <Box
                width={{ base: '100', tablet: '70' }}
                display="flex"
                childGap="xs"
                padding={{ base: '0 lg', tablet: 'md md md lg' }}
                justifyContent={{ base: queueStatusIsConditionallyApproved ? 'center' : 'flex-start' }}
                direction={{ base: 'column', tablet: 'row' }}
                gap="md"
                alignItems="center"
              >
                {enableQcPriority &&
                  formattedTarget &&
                  !decisionDate &&
                  (installPackageStatus === AccountRequirementStatus.submitted ||
                    installPackageStatus === AccountRequirementStatus.resubmitted) && (
                    <Box direction="row" alignItems="center" childGap="2xs" fontSize="sm" color="body-secondary">
                      <PalmettoIcon name="clockSquare" size="xs" className="text-text-secondary" />
                      <Box as="span">Target review:</Box>
                      <Box as="span" fontWeight="bold" color="body-primary">
                        {formattedTarget}
                      </Box>
                    </Box>
                  )}
              </Box>
            </Box>
            <Box
              width="40"
              padding={{ base: '0 lg', tablet: 'md lg' }}
              direction={{ base: 'column', tablet: 'row' }}
              childGap="xs"
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              {installPackageStatus !== AccountRequirementStatus.completed && (
                <Button
                  size="sm"
                  variant="secondary"
                  tone="neutral"
                  onClick={handleEditClickAction}
                  disabled={!allowsAction}
                >
                  Edit
                </Button>
              )}
              {installPackageStatus === AccountRequirementStatus.completed && (
                <Button size="sm" variant="secondary" tone="neutral" onClick={handleAfterCompletionRedirect}>
                  View
                </Button>
              )}
            </Box>
          </>
        ) : null}
      </Box>
    </Card>
  );
};
